import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const LS_KEY = "audrivio_cookie_consent_v1";

export default function CookieBanner() {
  const { t } = useLang();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (!stored) setVisible(true);
    } catch {
      setVisible(true);
    }
    const handler = () => {
      // reload existing prefs if any so user sees current choices
      try {
        const stored = localStorage.getItem(LS_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setPrefs({
            necessary: true,
            analytics: !!parsed.analytics,
            marketing: !!parsed.marketing,
          });
        }
      } catch { /* ignore */ }
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener("audrivio:open-cookie-banner", handler);
    return () => window.removeEventListener("audrivio:open-cookie-banner", handler);
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ ...choice, ts: new Date().toISOString() }));
    } catch {
      /* ignore storage errors */
    }
    setVisible(false);
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => persist(prefs);

  if (!visible || pathname.startsWith("/admin")) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:left-4 sm:right-4 md:left-6 md:right-6 lg:left-8 lg:right-8 z-[100] max-w-4xl mx-auto"
    >
      <div className="bg-xd-dark border border-xd-cyan/30 shadow-2xl shadow-black/40 rounded-2xl p-5 sm:p-6 text-slate-200 backdrop-blur-md">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-11 h-11 rounded-xl bg-xd-cyan/10 text-xd-cyan items-center justify-center flex-shrink-0 border border-xd-cyan/20">
            <Cookie className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan mb-1">GDPR</div>
                <h3 className="font-display text-lg font-semibold text-white">{t("cookie_title")}</h3>
              </div>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-close-btn"
                aria-label="Chiudi"
                className="text-slate-400 hover:text-white transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              {t("cookie_desc")}{" "}
              <Link to="/cookie" className="text-xd-cyan hover:underline">
                {t("cookie_more")}
              </Link>
            </p>

            {expanded && (
              <div className="mt-4 grid gap-2 border-t border-xd-darkBorder pt-4" data-testid="cookie-prefs">
                {[
                  { key: "necessary", disabled: true, label: t("cookie_pref_necessary"), desc: t("cookie_pref_necessary_desc") },
                  { key: "analytics", disabled: false, label: t("cookie_pref_analytics"), desc: t("cookie_pref_analytics_desc") },
                  { key: "marketing", disabled: false, label: t("cookie_pref_marketing"), desc: t("cookie_pref_marketing_desc") },
                ].map((row) => (
                  <label key={row.key} className="flex items-start gap-3 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prefs[row.key]}
                      disabled={row.disabled}
                      onChange={(e) => setPrefs((p) => ({ ...p, [row.key]: e.target.checked }))}
                      data-testid={`cookie-toggle-${row.key}`}
                      className="mt-1 accent-xd-cyan disabled:opacity-70"
                    />
                    <div>
                      <div className="text-sm font-medium text-white">{row.label}</div>
                      <div className="text-xs text-slate-400">{row.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-btn"
                className="btn-cta rounded-lg px-5 py-2.5 text-sm"
              >
                {t("cookie_accept")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-reject-btn"
                className="rounded-lg px-5 py-2.5 text-sm border border-xd-darkBorder text-slate-300 hover:border-xd-cyan hover:text-white transition-colors"
              >
                {t("cookie_reject")}
              </button>
              {expanded ? (
                <button
                  type="button"
                  onClick={saveCustom}
                  data-testid="cookie-save-btn"
                  className="rounded-lg px-5 py-2.5 text-sm border border-xd-cyan/50 text-xd-cyan hover:bg-xd-cyan/10 transition-colors"
                >
                  {t("cookie_save")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  data-testid="cookie-customize-btn"
                  className="rounded-lg px-5 py-2.5 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  {t("cookie_customize")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
